import { Request, Response } from "express";

import { School } from "../models";
import { logger } from "../utils/logger";
import { schoolIdValidation } from "../validation/auth-validation";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

class ValidationController {
  /**
   * Check if school code is valid and active
   */
  async checkSchoolCode(req: Request, res: Response): Promise<void> {
    try {
      // Validate request query
      const { error, value } = schoolIdValidation.validate(req.query);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0]?.message ?? "Invalid input",
        } as APIResponse);
        return;
      }

      const { schoolId } = value as { schoolId: string };

      // Find school with the ID
      const school = await School.findOne({
        _id: schoolId,
        verificationStatus: "approved",
        isDeleted: false,
      }).select("schoolName verificationStatus");

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found or inactive",
          data: {
            schoolId,
            valid: false,
          },
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "School ID is valid",
        data: {
          schoolId,
          valid: true,
          school: {
            id: school._id,
            schoolName: school.schoolName,
            verificationStatus: school.verificationStatus,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Check school ID error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get system health status
   */
  healthCheck(req: Request, res: Response): void {
    try {
      // Basic health check - could be expanded to check database connectivity, etc.
      const healthData = {
        status: "healthy",
        timestamp: new Date().toISOString(),
        version: process.env.APP_VERSION ?? "1.0.0",
        environment: process.env.NODE_ENV ?? "development",
        uptime: Math.floor(process.uptime()),
      };

      res.status(200).json({
        success: true,
        message: "Service is healthy",
        data: healthData,
      } as APIResponse);
    } catch (error) {
      logger.error("Health check error:", error);
      res.status(500).json({
        success: false,
        message: "Service is unhealthy",
        data: {
          status: "unhealthy",
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : "Unknown error",
        },
      } as APIResponse);
    }
  }

  /**
   * Get API version information
   */
  getVersion(req: Request, res: Response): void {
    try {
      const versionData = {
        version: process.env.APP_VERSION ?? "1.0.0",
        environment: process.env.NODE_ENV ?? "development",
        buildTime: process.env.BUILD_TIME,
        nodeVersion: process.version,
        apiVersion: "v1",
        documentation: `${process.env.FRONTEND_URL}/api/docs`,
      };

      res.status(200).json({
        success: true,
        message: "API version information",
        data: versionData,
      } as APIResponse);
    } catch (error) {
      logger.error("Version check error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to get version information",
      } as APIResponse);
    }
  }
}

export const validationController = new ValidationController();
